import { useEffect, useState, useMemo } from 'react';
import { Button, Divider, ImageList, ImageListItem } from '@mui/material';
import {
  TypeDetailInfo,
  VIEW_SEL_ITEM_KEY,
  VIEW_SEL_ITEM_PRICE_INFO,
} from '@/apis/cart/cart.interface';
import { useTranslation } from '@/i18n/client';
import { AttrUtils, AttrItemVo, AttrValueVo } from '../utils/attrUtils';
import JDIPrice from '@/components/price/index';
import { imageUtil } from '@/utils/imageUtil';
import { sendClickTrack, EXPOSURE_RECORD, sendKeyActionTrack } from '@/tracks/26438';
import { observer } from 'mobx-react';
import styles from './index.module.scss';
import cn from 'clsx';
import CDNImage from '../../cdnImage/cdnImage';
import ServiceList from '../../product/serviceList';
import { useStores } from '@/models';

interface ItemProps {
  skuId: string; // 当前购物车中商品规格的skuId
  handleClose?: () => void;
  btnText?: string;
  btnTextColor?: string;
  updateItemData?: (params: any) => void;
  typeDetailInfo?: TypeDetailInfo | null;
  handleClickType: (item: AttrValueVo, skuId?: string, currentChecked?: boolean) => void; //更新规格
  onSeriesSelect: (skus: string[]) => void; // 切换商品系列
  isShowService?: boolean; // 是否显示服务 默认false 不展示
}
interface SeriesProps {
  id: string;
  name: string;
  skus: string[];
}

const ProTypeModal: React.FC<ItemProps> = observer(
  ({
    skuId,
    typeDetailInfo,
    btnText,
    btnTextColor,
    handleClose,
    updateItemData,
    handleClickType,
    onSeriesSelect,
    isShowService = true,
  }) => {
    const { t } = useTranslation();
    const [attrItemVo, setAttrItemVo] = useState<AttrItemVo[]>(); // 选择切换商品类型接口数据
    const [seriesModule, setSeriesModule] = useState<SeriesProps[] | undefined>([]); // 切换商品系列
    const { serviceListStore } = useStores();
    const { getServiceParam } = serviceListStore;

    useEffect(() => {
      setSeriesModule(typeDetailInfo?.seriesModule?.series);
      const newData = AttrUtils.initAttrListBySkuId(
        typeDetailInfo?.selectedModule?.colorSizePartList || [],
        typeDetailInfo?.commonModule?.['C-M#viewCommonItem&commonCore']?.skuId || '',
      );
      setAttrItemVo(newData);
    }, [typeDetailInfo]);

    const skuStatus = useMemo(() => {
      return typeDetailInfo?.selectedModule?.skuStatus;
    }, [typeDetailInfo]);

    const disableUpdateButton = useMemo(() => {
      return typeDetailInfo?.commonModule?.['C-M#viewCommonItem&commonCore']?.disableUpdateButton;
    }, [typeDetailInfo]);

    const titleAndPriceFloor = () => {
      const priceItem = typeDetailInfo?.priceModule?.[VIEW_SEL_ITEM_PRICE_INFO];
      const selectedItem = typeDetailInfo?.selectedModule?.[VIEW_SEL_ITEM_KEY];

      const hasMemberPrice = priceItem?.memberPromotion;
      const hasCrossOffDiscount = selectedItem?.mainPrice !== selectedItem?.crossOffPrice;
      return (
        <div style={{ marginLeft: '12px' }}>
          <div className={styles['name']}>{selectedItem?.salesDesc}</div>
          <div className={styles['price-item']}>
            {/* 到手价 */}
            <JDIPrice
              locale={typeDetailInfo?.localInfo?.languageCode}
              currency={typeDetailInfo?.localInfo?.currency}
              price={selectedItem?.mainPrice || ''}
              uiType={2}
              symbolStyle={{
                fontSize: '24px',
              }}
              containerStyle={{
                fontSize: '32px',
                lineHeight: '32px',
                marginRight: '4px',
                color: hasMemberPrice || hasCrossOffDiscount ? '#CC0C1C' : '#1A1A1A',
              }}
            />
            {priceItem?.pricePlusTips && hasMemberPrice && (
              <span style={{ color: '#CC0C1C' }}>{priceItem?.pricePlusTips}</span>
            )}
          </div>
          {/* 折扣等标签 */}
          <div className={styles.discountTag}>
            {/* 划线价 */}
            {hasCrossOffDiscount && (
              <JDIPrice
                locale={typeDetailInfo?.localInfo?.languageCode}
                currency={typeDetailInfo?.localInfo?.currency}
                price={selectedItem?.crossOffPrice || ''}
                uiType={2}
                containerStyle={{
                  fontSize: '16px',
                  color: '#1A1A1A',
                  marginRight: '4px',
                  fontWeight: '600',
                }}
                lineThrough
              />
            )}
            {priceItem?.percentReductionDescIntergation && (
              <>
                <Divider
                  orientation='vertical'
                  flexItem
                  style={{ alignSelf: 'center', height: '12px', margin: '0 8px' }}
                />
                <div className={styles['tag']}>{priceItem?.percentReductionDescIntergation}</div>
              </>
            )}
          </div>
        </div>
      );
    };

    return (
      typeDetailInfo && (
        <div>
          <div className={styles['container']}>
            <div className={styles.cartItemInfo}>
              <CDNImage
                src={typeDetailInfo?.selectedModule?.['C-M#viewSelectedItem&core']?.imageUrl || ''}
                width={100}
                height={100}
                alt='product_type_img'
                style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '4px',
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />
              {/* title及价格楼层 */}
              {titleAndPriceFloor()}
            </div>
            {!!seriesModule?.length && (
              <div key='series'>
                <div className={styles['color-space']}>
                  {`${t('dp.selectedGroup.series')}: `}
                  <span className={styles['color-item']}>
                    {seriesModule.find((item) => item.skus.includes(skuId))?.name}
                  </span>
                </div>
                <div className={styles['content']}>
                  {seriesModule.map((item) => {
                    const isActive = item?.skus.includes(String(skuId));
                    return (
                      <div
                        key={item.id}
                        className={`${styles['item']} ${isActive ? styles['active'] : ''}`}
                        onClick={() => !isActive && onSeriesSelect(item.skus)}
                      >
                        {item.name}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* 规格楼层 */}
            {attrItemVo?.map((item, index) => (
              <div key={index}>
                <div className={styles['color-space']}>
                  {`${item?.title}: `}
                  <span className={styles['color-item']}>
                    {item?.itemList?.find((p) => item?.currentValue === p?.key)?.label}
                  </span>
                </div>
                {item?.itemList?.length === item?.itemList?.filter((p) => p.imgUrl).length ? (
                  <ImageList
                    cols={5}
                    rowHeight={73}
                    style={{
                      overflowY: 'hidden',
                      gap: '8px',
                      marginTop: '12px',
                      marginBottom: '20px',
                      marginRight: '53px',
                    }}
                  >
                    {item?.itemList?.map((partItem, partIndex) => {
                      const isActive = item?.currentValue === partItem?.key;
                      let noStock = false;
                      if (partItem?.matchSkuId) {
                        if (skuStatus) {
                          noStock = skuStatus?.[partItem?.matchSkuId] !== '0';
                        }
                      }
                      return (
                        <ImageListItem
                          key={partIndex}
                          className={`${partItem?.disabled ? styles['imgDisabled'] : ''}`}
                          style={{
                            borderWidth: isActive ? '1.5px' : '1px',
                            borderStyle: !noStock ? 'solid' : !isActive ? 'dashed' : 'solid',
                            alignItems: 'center',
                            width: '80px',
                            height: '100px',
                            cursor: partItem?.disabled ? 'not-allowed' : 'pointer',
                            borderColor:
                              item?.currentValue === partItem?.key
                                ? '#1A1A1A'
                                : partItem?.disabled
                                  ? '#E0E1E5'
                                  : '#E0E1E5',
                            borderRadius: '5px',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                          onClick={
                            !partItem?.disabled
                              ? () =>
                                  handleClickType(
                                    partItem,
                                    skuId,
                                    item?.currentValue === partItem?.key,
                                  )
                              : () => {}
                          }
                        >
                          <CDNImage
                            src={`${imageUtil.concatImgUrlWithoutForceHttps(partItem.imgUrl ? partItem.imgUrl : '', 140, 140)}`}
                            width={70}
                            height={70}
                            alt='product_type_item_img'
                            style={{
                              width: '80px',
                              height: '80px',
                              objectFit: 'contain',
                              flexShrink: 0,
                              opacity: partItem?.disabled || noStock ? 0.4 : 1,
                            }}
                          />
                          <div
                            className={styles.imgBottomText}
                            style={{
                              backgroundColor: isActive ? '#F7F8FC' : 'transparent',
                              opacity: partItem?.disabled || noStock ? 0.4 : 1,
                              fontWeight: isActive ? '700' : '400',
                            }}
                          >
                            {partItem?.key}
                          </div>
                        </ImageListItem>
                      );
                    })}
                    {item?.itemList?.map(() => {
                      return null;
                    })}
                  </ImageList>
                ) : (
                  <div className={styles['content']}>
                    {item?.itemList?.map((partItem, partIndex) => {
                      const isActive = item?.currentValue === partItem?.key;
                      const noStock =
                        partItem?.matchSkuId && skuStatus?.[partItem?.matchSkuId] !== '0';
                      return (
                        <div
                          key={partIndex}
                          className={`${styles['item']} ${isActive ? styles['active'] : partItem?.disabled ? styles['disabled'] : ''} ${
                            noStock ? styles['noStock'] : ''
                          }`}
                          onClick={
                            !partItem?.disabled
                              ? () => handleClickType(partItem, partItem?.matchSkuId, isActive)
                              : () => {}
                          }
                        >
                          {partItem.label}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            {/* 服务楼层 */}
            {isShowService &&
              typeDetailInfo?.serviceModule &&
              typeDetailInfo?.serviceModule?.serviceItems?.length > 0 &&
              typeDetailInfo?.localInfo && (
                <ServiceList
                  localInfo={typeDetailInfo?.localInfo}
                  serviceData={typeDetailInfo?.serviceModule}
                  mainSkuId={skuId}
                />
              )}
          </div>
          <div className={styles.bottom}>
            <Button
              className={cn(
                styles.buyButton,
                (skuStatus && skuStatus?.[skuId] !== '0') || disableUpdateButton
                  ? styles['disabled']
                  : '',
              )}
              disabled={(skuStatus && skuStatus?.[skuId] !== '0') || disableUpdateButton}
              style={{ background: btnTextColor ? btnTextColor : 'var(--sys-Text-Primary)' }}
              onClick={() => {
                handleClose && handleClose();
                const { addonProducts } = getServiceParam();
                updateItemData &&
                  updateItemData({
                    skuId,
                    addonProducts: addonProducts && addonProducts?.length > 0 ? addonProducts : [],
                  });
                sendClickTrack.ProductSpecLayer_MainBtn_Click({
                  skuid: skuId,
                  spuid:
                    typeDetailInfo?.commonModule?.['C-M#viewCommonItem&commonCore']?.spuId || '',
                });
              }}
            >
              {btnText ? btnText : t('cart.addCart')}
            </Button>
          </div>
        </div>
      )
    );
  },
);

export default ProTypeModal;
